/**
 * One resource group on its own: the chart at full size, and the works orders behind the
 * selected week laid out full-width directly beneath it.
 *
 * The meta line is the whole denominator argument in one sentence — whose week, how many
 * hours, how much was already late, how much is scheduled — so the numbers above it never
 * have to be taken on trust.
 */

import React from 'react'
import Legend from './Legend'
import ResourceChart from './ResourceChart'
import WeekPanel from './WeekPanel'
import type { ResourceModel } from '../../viewmodels/loading'
import { hrs, weekLabel } from './format'

function Badge({ r }: { r: ResourceModel }): React.JSX.Element {
  const clears = r.clearIdx >= 0
  return (
    <span
      className="whitespace-nowrap rounded-[4px] px-[7px] py-[3px] font-mono text-[9.5px] font-semibold uppercase tracking-[.06em]"
      style={{
        background: clears ? 'var(--lc-teal-bg)' : 'var(--lc-red-bg)',
        color: clears ? 'var(--lc-teal-ink)' : 'var(--lc-red-ink)',
      }}
    >
      {clears ? `clears ${weekLabel(r.weeks[r.clearIdx].weekStart)}` : `never clears in ${r.weeks.length}wk`}
    </span>
  )
}

function Head({ r, onBack }: { r: ResourceModel; onBack: () => void }): React.JSX.Element {
  const meta = [
    r.site,
    `${hrs(r.capacity)} per standard week`,
    `${hrs(r.overdue)} overdue carried in`,
    `${hrs(r.totalScheduled)} scheduled across ${r.weeks.length} weeks`,
  ].join(' · ')
  return (
    <div className="flex items-start justify-between gap-6 px-[22px] pt-4">
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-[9px]">
          <span className="font-sans text-[19px] font-semibold leading-[1.15] tracking-[-.01em]" style={{ color: 'var(--lc-ink)' }}>{r.name}</span>
          <span className="font-mono text-[12px]" style={{ color: 'var(--lc-mut)' }}>{r.sub}</span>
          <Badge r={r} />
        </div>
        <div className="font-mono text-[11.5px] leading-[1.4]" style={{ color: 'var(--lc-mut)' }}>{meta}</div>
      </div>
      <button
        type="button"
        onClick={onBack}
        className="whitespace-nowrap rounded-md border px-[11px] py-1.5 font-sans text-[11px] font-semibold"
        style={{ borderColor: 'var(--lc-b12)', background: 'var(--lc-card)', color: 'var(--lc-ink3)' }}
      >
        ← Back to grid
      </button>
    </div>
  )
}

export default function ResourceView({ r, levelled, week, wo, onWeek, onWo, onBack }: {
  r: ResourceModel
  levelled: boolean
  week: number
  wo: string | null
  onWeek: (index: number) => void
  onWo: (wo: string | null) => void
  onBack: () => void
}): React.JSX.Element {
  return (
    <div>
      <Head r={r} onBack={onBack} />
      <div className="flex flex-col gap-4 px-[22px] pb-5 pt-3.5">
        <ResourceChart r={r} levelled={levelled} week={week} onSelect={onWeek}>
          <Legend levelled={levelled} where="detail" />
        </ResourceChart>
        <WeekPanel
          r={r}
          week={week}
          onWeek={onWeek}
          wo={wo}
          onWo={(next) => onWo(next === wo ? null : next)}
        />
      </div>
    </div>
  )
}
